import { useCallback, useEffect, useState } from "react";
import { api } from "../api/apiClient";
import type { ForwardDemandView } from "../api/types";
import { useAuth } from "../state/AuthContext";
import { forwardDemandCount } from "../utils/forwardDemandCount";

/**
 * G-43: the two-week forward look, rendered where ordering happens — beside
 * the low-stock panel on the Inventory page and the new-delivery page. Shows
 * what to BUY (with the date it is needed for, and the incoming stock that
 * was already counted so nothing is ordered twice), what can be MOVED
 * between shops instead, and which orders cannot be checked at all.
 * Advisory only: it reports, it never holds stock.
 */
export function ForwardDemandPanel({
  view: viewFromHost,
  bare = false,
}: {
  /**
   * G-132: the inventory page fetches the forward look itself (it needs the
   * count for its "To order" chip before this renders) and hands it down, so
   * the panel does not ask twice. Omit it and the panel fetches, as the
   * new-delivery page still expects.
   */
  view?: ForwardDemandView;
  /** G-132: drop the title — the chip that opened this view already names it. */
  bare?: boolean;
} = {}) {
  const { hasRole } = useAuth();
  const visible = hasRole("OWNER", "MANAGER");
  const controlled = viewFromHost !== undefined;
  const [ownView, setView] = useState<ForwardDemandView | null>(null);
  const view = viewFromHost ?? ownView;
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setView(await api.getForwardDemand());
      setError(null);
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not check the scheduled orders.",
      );
    }
  }, []);

  useEffect(() => {
    if (visible && !controlled) void refresh();
  }, [visible, controlled, refresh]);

  if (!visible) return null;
  if (!controlled && error) return <div className="alert error">{error}</div>;
  if (!view) return null;

  const uncheckable = view.orders.filter((o) => o.verdict === "UNCHECKABLE");
  const shortOrders = view.orders.filter((o) => o.verdict === "SHORT");
  if (forwardDemandCount(view) === 0) {
    return null;
  }

  return (
    <div className="card" style={{ display: "grid", gap: "var(--space-3)" }}>
      <div>
        {bare ? null : <strong>Scheduled orders, next {view.horizonDays} days</strong>}
        <p className="muted" style={{ margin: 0 }}>
          {bare ? `Next ${view.horizonDays} days. ` : ""}
          {shortOrders.length === 0
            ? "Every checkable order is covered by stock on hand or incoming."
            : `${shortOrders.length} ${
                shortOrders.length === 1 ? "order" : "orders"
              } cannot be fulfilled without buying.`}
        </p>
      </div>

      {view.purchases.length > 0 ? (
        <div>
          <strong>What to order</strong>
          <ul className="forward-demand-list">
            {view.purchases.map((p) => (
              <li key={`${p.locationId}-${p.description}`}>
                {Number(p.qty)} × {p.description} for {p.locationName} — needed
                by {p.neededBy}
                {p.incomingCounted.length > 0 ? (
                  <span className="muted">
                    {" "}
                    (already counted:{" "}
                    {p.incomingCounted
                      .map(
                        (inc) =>
                          `${Number(inc.qty)} arriving ${inc.arrivesAt} from ${inc.supplierName}`,
                      )
                      .join("; ")}
                    )
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {view.transfers.length > 0 ? (
        <div>
          <strong>Move between shops instead of buying</strong>
          <ul className="forward-demand-list">
            {view.transfers.map((t, index) => (
              <li key={`${t.description}-${index}`}>
                {Number(t.qty)} × {t.description}: {t.fromLocationName} →{" "}
                {t.toLocationName} before {t.neededBy}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {uncheckable.length > 0 ? (
        <div>
          <strong>Cannot be checked</strong>
          <ul className="forward-demand-list">
            {uncheckable.map((o) => (
              <li key={o.orderId}>
                {o.code} ({o.scheduledDate}, {o.locationName}):{" "}
                <span className="muted">{o.uncheckable.join("; ")}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
